// frontend/src/utils/useSeriesOptions.ts
import { useMemo } from 'react';
import { useAppStore } from '../state/store';
import { indicatorDisplayRegistry } from './indicatorDisplayRegistry'; // ← direct import!
import { getEmittedFields } from './indicatorDisplayRegistry';
import type { Indicator as IndicatorType } from '../state/types';

export type SeriesOption = {
  id: string;
  label: string;
  type: 'instrument' | 'indicator';
  emittedFields: Record<string, 'Number' | 'String'>;
};

export function useSeriesOptions(currentIndicatorId?: string): SeriesOption[] {
  const instruments = useAppStore(s => s.instruments);
  const indicators = useAppStore(s => s.indicators);

  return useMemo(() => {
    const options: SeriesOption[] = [];

    // ── Instruments ─────────────────────────────────────
    instruments.forEach(inst => {
      options.push({
        id: inst.id,
        label: inst.symbol,
        type: 'instrument',
        emittedFields: getEmittedFields('instrument'),
      });
    });

    // ── Indicators (exclude self) ───────────────────────
    indicators.forEach((ind: IndicatorType) => {
      if (currentIndicatorId && ind.id === currentIndicatorId) return;

      const displayInfo = indicatorDisplayRegistry[ind.type];
      const symbol = instruments.find(i => i.id === ind.instrumentId)?.symbol ?? '???';

      const label = ind.name
        ? ind.name
        : displayInfo?.nameGenerator
          ? displayInfo.nameGenerator(ind.config, symbol)
          : `${symbol} ${ind.type}`;

      options.push({
        id: ind.id,
        label,
        type: 'indicator',
        emittedFields: getEmittedFields(ind.type),
      });
    });

    // Sort alphabetically
    options.sort((a, b) => a.label.localeCompare(b.label));

    return options;
  }, [instruments, indicators, currentIndicatorId]);
}

/**
 * Filter series options to those that emit all required fields as Number.
 * Used e.g. by ADX editor to allow only OHLC-capable sources (High, Low, Close).
 */
export function filterSeriesOptionsByEmittedFields(
  options: SeriesOption[],
  requiredFields: string[]
): SeriesOption[] {
  return options.filter((opt) =>
    requiredFields.every(
      (f) => opt.emittedFields[f] === 'Number'
    )
  );
}